import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CarouselContainer,
  CarouselTrack,
  LogoContainer,
  Logo,
} from './styles';

const Carousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    currentX: 0,
    animationId: 0,
  });

  const logoFiles = [
    'logo-starrett.png',
    'logo-torra.png',
    'logo-dasa.png',
    'logo-xp.png',
    'logo-itaipu.png',
    'logo-folha.png',
    'logo-usp.png',
    'logo-rappi.png',
    'logo-sivtec.png',
    'logo-neoenergia.png',
    'logo-mare.png',
    'logo-devflix.png',
    'logo-revistaoeste.png',
    'logo-agi.png',
  ];

  const getCarouselSettings = useCallback(() => {
    if (windowWidth <= 600) {
      return { logoWidth: 120, visibleLogos: 3 };
    } else if (windowWidth <= 1024) {
      return { logoWidth: 200, visibleLogos: 3 };
    } else if (windowWidth <= 1419) {
      return { logoWidth: 280, visibleLogos: 4 };
    } else {
      return { logoWidth: 250, visibleLogos: 5 };
    }
  }, [windowWidth]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { logoWidth, visibleLogos } = getCarouselSettings();

  const extendedLogos = [...logoFiles, ...logoFiles.slice(0, visibleLogos)];

  const resetToStart = useCallback(() => {
    if (trackRef.current) {
      trackRef.current.style.transition = 'none';
      trackRef.current.style.transform = `translateX(0px)`;
      setTimeout(() => {
        if (trackRef.current) {
          trackRef.current.style.transition = 'transform 0.5s ease-in-out';
        }
      }, 50);
    }
    setCurrentIndex(0);
  }, []);

  const startAutoplay = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!dragStateRef.current.isDragging) {
        setCurrentIndex(prevIndex => {
          const newIndex = prevIndex + 1;
          if (newIndex >= logoFiles.length) {
            setTimeout(resetToStart, 500);
          }
          return newIndex;
        });
      }
    }, 3000);
  }, [logoFiles.length, resetToStart]);

  const stopAutoplay = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const updateTransform = useCallback(() => {
    if (!trackRef.current || !dragStateRef.current.isDragging) return;

    const offset = dragStateRef.current.currentX - dragStateRef.current.startX;
    const translateX = -currentIndex * logoWidth + offset;

    trackRef.current.style.transform = `translateX(${translateX}px)`;

    dragStateRef.current.animationId = requestAnimationFrame(updateTransform);
  }, [currentIndex, logoWidth]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      dragStateRef.current.isDragging = true;
      dragStateRef.current.startX = e.clientX;
      dragStateRef.current.currentX = e.clientX;

      if (trackRef.current) {
        trackRef.current.style.transition = 'none';
      }

      stopAutoplay();
      updateTransform();
    },
    [stopAutoplay, updateTransform],
  );

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStateRef.current.isDragging) return;

    dragStateRef.current.currentX = e.clientX;
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!dragStateRef.current.isDragging) return;

    dragStateRef.current.isDragging = false;
    cancelAnimationFrame(dragStateRef.current.animationId);

    const offset = dragStateRef.current.currentX - dragStateRef.current.startX;
    const threshold = logoWidth / 4;

    if (trackRef.current) {
      trackRef.current.style.transition = 'transform 0.5s ease-in-out';
    }

    if (offset > threshold) {
      setCurrentIndex(prevIndex => {
        const newIndex = prevIndex - 1;
        return newIndex < 0 ? logoFiles.length - 1 : newIndex;
      });
    } else if (offset < -threshold) {
      setCurrentIndex(prevIndex => {
        const newIndex = prevIndex + 1;
        if (newIndex >= logoFiles.length) {
          setTimeout(resetToStart, 500);
          return newIndex;
        }
        return newIndex;
      });
    }

    setTimeout(startAutoplay, 1000);
  }, [logoWidth, logoFiles.length, resetToStart, startAutoplay]);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (trackRef.current && !dragStateRef.current.isDragging) {
      const translateX = -currentIndex * logoWidth;
      trackRef.current.style.transform = `translateX(${translateX}px)`;
    }
  }, [currentIndex, logoWidth]);

  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
  }, [startAutoplay, stopAutoplay]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [windowWidth]);

  return (
    <CarouselContainer
      onMouseDown={handleMouseDown}
      style={{
        cursor: dragStateRef.current.isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
    >
      <CarouselTrack ref={trackRef} translateX={0}>
        {extendedLogos.map((logo, index) => (
          <LogoContainer key={`${logo}-${index}`}>
            <Logo
              src={`/images/customers/${logo}`}
              alt={`Cliente ${(index % logoFiles.length) + 1}`}
              onError={e => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
              onLoad={e => {
                (e.target as HTMLImageElement).style.opacity = '1';
              }}
              draggable={false}
            />
          </LogoContainer>
        ))}
      </CarouselTrack>
    </CarouselContainer>
  );
};

export default Carousel;
